export type GrammarRole = 'noun' | 'verb' | 'adjective';

export interface GrammarWord {
  readonly id: string;
  readonly text: string;
}

export interface GrammarGladeChallenge {
  readonly id: string;
  readonly skillId: string;
  readonly arrival: string;
  readonly words: readonly GrammarWord[];
  readonly correctOrder: readonly string[];
  readonly correctRoles: Readonly<Record<GrammarRole, string>>;
  readonly punctuationOptions: readonly string[];
  readonly correctPunctuation: string;
  readonly hints: readonly string[];
  readonly successMessage: string;
}

export const GRAMMAR_GLADE_RESTORATION_EVENT = 'grammar-glade-lanterns-restored';

export const GRAMMAR_GLADE_CHALLENGES: readonly GrammarGladeChallenge[] = [
  {
    id: 'lantern-sentence',
    skillId: 'language-sentence-structure-1',
    arrival: 'The first lantern needs a complete telling sentence.',
    words: [
      { id: 'glows', text: 'glows' },
      { id: 'bright', text: 'bright' },
      { id: 'the', text: 'The' },
      { id: 'lantern', text: 'lantern' },
    ],
    correctOrder: ['the', 'bright', 'lantern', 'glows'],
    correctRoles: { noun: 'lantern', verb: 'glows', adjective: 'bright' },
    punctuationOptions: ['.', '?', '!'],
    correctPunctuation: '.',
    hints: [
      'Begin with “The.” The word that tells what kind comes before the naming word.',
      '“Lantern” names a thing. “Glows” tells what it does.',
      'This sentence tells something calmly, so it ends with a period.',
    ],
    successMessage: 'The telling sentence is complete. One leaf lantern shines!',
  },
  {
    id: 'fox-question',
    skillId: 'language-sentence-structure-2',
    arrival: 'The next lantern is waiting for a complete asking sentence.',
    words: [
      { id: 'fox', text: 'fox' },
      { id: 'can', text: 'Can' },
      { id: 'hop', text: 'hop' },
      { id: 'small', text: 'small' },
      { id: 'the', text: 'the' },
    ],
    correctOrder: ['can', 'the', 'small', 'fox', 'hop'],
    correctRoles: { noun: 'fox', verb: 'hop', adjective: 'small' },
    punctuationOptions: ['.', '?', '!'],
    correctPunctuation: '?',
    hints: [
      'An asking sentence can begin with “Can.”',
      '“Fox” names an animal. “Hop” tells the action. “Small” tells what kind.',
      'An asking sentence ends with a question mark.',
    ],
    successMessage: 'The question is complete. A second lantern lights the path!',
  },
  {
    id: 'owl-exclamation',
    skillId: 'language-sentence-structure-3',
    arrival: 'The final lantern needs a sentence with strong feeling.',
    words: [
      { id: 'owl', text: 'owl' },
      { id: 'hoots', text: 'hoots' },
      { id: 'a', text: 'A' },
      { id: 'brave', text: 'brave' },
    ],
    correctOrder: ['a', 'brave', 'owl', 'hoots'],
    correctRoles: { noun: 'owl', verb: 'hoots', adjective: 'brave' },
    punctuationOptions: ['.', '?', '!'],
    correctPunctuation: '!',
    hints: [
      'Begin with “A.” Put the describing word before the naming word.',
      '“Owl” names an animal. “Hoots” tells the action. “Brave” describes the owl.',
      'Strong feeling can end with an exclamation mark.',
    ],
    successMessage: 'The strong sentence is complete. Every glade lantern is glowing!',
  },
];

export function validateGrammarGladeChallenges(challenges = GRAMMAR_GLADE_CHALLENGES) {
  if (challenges.length < 3) throw new RangeError('Grammar Glade requires at least three challenges.');
  const ids = new Set<string>();
  challenges.forEach(challenge => {
    if (ids.has(challenge.id)) throw new RangeError(`Duplicate Grammar Glade challenge: ${challenge.id}`);
    ids.add(challenge.id);
    const wordIds = challenge.words.map(word => word.id);
    if (new Set(wordIds).size !== wordIds.length || wordIds.some(id => !id.trim())) {
      throw new RangeError(`Grammar Glade challenge ${challenge.id} has invalid word ids.`);
    }
    if (challenge.correctOrder.length !== wordIds.length
      || challenge.correctOrder.some(id => !wordIds.includes(id))) {
      throw new RangeError(`Grammar Glade challenge ${challenge.id} has an invalid sentence order.`);
    }
    (Object.keys(challenge.correctRoles) as GrammarRole[]).forEach(role => {
      if (!wordIds.includes(challenge.correctRoles[role])) {
        throw new RangeError(`Grammar Glade challenge ${challenge.id} has an invalid ${role}.`);
      }
    });
    if (!challenge.punctuationOptions.includes(challenge.correctPunctuation)) {
      throw new RangeError(`Grammar Glade challenge ${challenge.id} has invalid punctuation.`);
    }
    if (challenge.hints.length < 3) throw new RangeError(`Grammar Glade challenge ${challenge.id} needs progressive hints.`);
  });
  return true;
}

validateGrammarGladeChallenges();
